import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Save, Download, Mail, Loader2, Eye, Upload, X, Image as ImageIcon } from "lucide-react";
import { usePdfPreview } from "@/components/PdfPreviewDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

// ---------- number formatting helpers ----------
const toNum = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return v;
  const cleaned = String(v).replace(/[$,\s%]/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};
const fmtMoney = (v) => {
  const n = toNum(v);
  if (n === null) return "—";
  return `$${Math.round(n).toLocaleString("en-US")}`;
};
const fmtPct = (v) => {
  const n = toNum(v);
  if (n === null) return "—";
  return `${(n * 100).toFixed(n < 0.001 ? 3 : 2)}%`;
};
const fmtInt = (v) => {
  const n = toNum(v);
  if (n === null) return "—";
  return Math.round(n).toLocaleString("en-US");
};
const fmtCell = (v, fmt) => {
  if (v === null || v === undefined || v === "") return "";
  const n = toNum(v);
  if (n !== null) {
    if ((fmt || "").includes("%")) return fmtPct(n);
    if ((fmt || "").match(/\$|USD/)) return fmtMoney(n);
    if (Math.abs(n) > 999) return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
    return `${n}`;
  }
  return String(v);
};

// ---------- product columns on the summary sheet ----------
// The order here is the canonical column layout on the workbook Summary
// sheet (E..K → columns 14/15 = premium / penetration). Renaming a product
// only changes the DISPLAY label — the underlying cell ref is unchanged so
// the workbook math keeps flowing.
const DEFAULT_PRODUCTS = [
  { key: "VSC",    premiumRef: "E14", penetrationRef: "E15", label: "VSC" },
  { key: "MNT",    premiumRef: "F14", penetrationRef: "F15", label: "MNT" },
  { key: "ANC",    premiumRef: "G14", penetrationRef: "G15", label: "ANC" },
  { key: "Bundle", premiumRef: "H14", penetrationRef: "H15", label: "Bundle" },
  { key: "Paint",  premiumRef: "I14", penetrationRef: "I15", label: "Paint" },
  { key: "LTW",    premiumRef: "J14", penetrationRef: "J15", label: "LTW" },
  { key: "UV",     premiumRef: "K14", penetrationRef: "K15", label: "UV" },
];
const PRODUCTS = DEFAULT_PRODUCTS;

// Refs the user explicitly asked to be editable on the summary page
const EDITABLE_REFS = new Set([
  "E9",   // Annual Vehicle Sales
  "E11",  // Cancellation percentage
  ...PRODUCTS.flatMap((p) => [p.premiumRef, p.penetrationRef]),
  "E17",  // Loss ratio (all products)
  "E19",  // Investment Percentage — cascades through Year 1..10 sheets
]);

// Percentage cells (rendered as % on read-back / display)
const PERCENT_REFS = new Set([
  "E11", "E17", "E19",
  ...PRODUCTS.map((p) => p.penetrationRef),
]);

// Read a value by ref out of the cells matrix
function buildCellMap(sheet) {
  const map = {};
  (sheet?.cells || []).forEach((row) => row.forEach((cell) => { map[cell.ref] = cell; }));
  return map;
}

// Format the "raw" model value for the editable input control. Percentages
// are stored as decimals in the workbook (0.05 = 5%) but shown as 5.
const editableString = (raw, ref) => {
  if (raw === "" || raw === null || raw === undefined) return "";
  const n = toNum(raw);
  if (n === null) return String(raw);
  if (PERCENT_REFS.has(ref)) return `${+(n * 100).toFixed(4)}`;
  return `${n}`;
};

// Convert the string the user typed back into the value we persist. For
// percent cells, "5" → "0.05".
const persistString = (typed, ref) => {
  if (typed === "" || typed === null || typed === undefined) return "";
  const cleaned = String(typed).replace(/[$,\s%]/g, "");
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return String(typed);
  if (PERCENT_REFS.has(ref)) return `${n / 100}`;
  return `${n}`;
};

// ---------- structured summary sheet ----------
function SummarySheet({ sheet, inputs, computed, productLabels, productSold, onSet, onLabelChange, onSoldToggle }) {
  const cells = useMemo(() => buildCellMap(sheet), [sheet]);
  const sheetName = sheet.name;

  const labelFor = (p) => (productLabels?.[p.key] || p.label || p.key);
  const isSold = (p) => productSold?.[p.key] !== false; // default true (sold)

  const readValue = (ref) => {
    const overridden = inputs?.[sheetName]?.[ref];
    if (overridden !== undefined && overridden !== "") return overridden;
    const cVal = computed?.[sheetName]?.[ref];
    if (cVal !== undefined && cVal !== null && cVal !== "") return cVal;
    return cells[ref]?.value ?? "";
  };

  const editInput = (ref, kind, disabled = false) => {
    const raw = inputs?.[sheetName]?.[ref] ?? cells[ref]?.value ?? "";
    const display = editableString(raw, ref);
    return (
      <div className="relative">
        <input
          type="text"
          inputMode={kind === "int" ? "numeric" : "decimal"}
          value={disabled ? "" : display}
          disabled={disabled}
          placeholder={disabled ? "—" : ""}
          onChange={(e) => onSet(sheetName, ref, persistString(e.target.value, ref))}
          className={`w-full h-11 rounded-sm border text-right pr-8 pl-3 focus:outline-none focus:ring-2 transition-shadow ${
            disabled
              ? "border-[#E9ECEF] bg-[#F8F9FA] text-[#6C757D] cursor-not-allowed"
              : "border-[#D4AF37]/50 bg-[#D4AF37]/8 text-[#0B132B] font-bold focus:ring-[#D4AF37]"
          }`}
          data-testid={`proforma-input-${sheetName}-${ref}`}
        />
        {kind === "pct" && !disabled && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#0B132B] font-bold text-sm pointer-events-none">%</span>}
        {kind === "money" && !disabled && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0B132B] font-bold text-sm pointer-events-none">$</span>}
      </div>
    );
  };

  // Calculated rows — display only
  const outputRow = (ref, label, opts = {}) => {
    const val = fmtMoney(readValue(ref));
    return (
      <div className={`flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-[#E9ECEF] ${opts.highlight ? "bg-[#D4AF37]/15 border-[#D4AF37]/40" : "bg-white"}`}
        data-testid={`proforma-output-${sheetName}-${ref}`}>
        <div className={`text-sm sm:text-[15px] ${opts.highlight ? "font-black uppercase tracking-[0.06em] text-[#0B132B]" : "font-semibold text-[#0B132B]"}`}>{label}</div>
        <div className={`font-mono tabular-nums text-right whitespace-nowrap ${opts.highlight ? "text-lg sm:text-2xl font-black text-[#0B132B]" : "text-base font-bold text-[#0B132B]"}`}>{val}</div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="proforma-summary">
      {/* Header banner */}
      <div className="bg-[#0B132B] text-white px-5 sm:px-8 py-6 rounded-sm">
        <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] font-bold">Advantage Warranty Group</div>
        <h2 className="font-display text-3xl sm:text-5xl font-black tracking-tight leading-none mt-2">10 Year Proforma</h2>
        <p className="text-xs text-white/70 mt-3 max-w-xl">Adjust the yellow input cells below — every number in the calculated section (and all Year 1‑10 sheets) recomputes when you press Save & recalc.</p>
      </div>

      {/* Assumptions */}
      <Card className="p-0 border-[#E9ECEF] rounded-sm overflow-hidden">
        <div className="bg-[#F8F9FA] border-b border-[#E9ECEF] px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.24em] font-bold text-[#0B132B]">Assumptions</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#6C757D]">Yellow = editable</div>
        </div>

        {/* Annual vehicle sales — the anchor input */}
        <div className="px-4 sm:px-6 py-4 border-b border-[#E9ECEF] bg-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <div className="text-sm sm:text-base font-bold text-[#0B132B]">Annual Vehicle Sales</div>
              <div className="text-[11px] text-[#6C757D] mt-0.5">All downstream volume is driven by this number</div>
            </div>
            <div className="w-full sm:w-40">{editInput("E9", "int")}</div>
          </div>
        </div>

        {/* Cancellation % */}
        <div className="px-4 sm:px-6 py-4 border-b border-[#E9ECEF] bg-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <div className="text-sm sm:text-base font-bold text-[#0B132B]">Cancellation percentage</div>
              <div className="text-[11px] text-[#6C757D] mt-0.5">Share of contracts that cancel each year</div>
            </div>
            <div className="w-full sm:w-40">{editInput("E11", "pct")}</div>
          </div>
        </div>

        {/* Product table */}
        <div className="px-4 sm:px-6 py-4 border-b border-[#E9ECEF] bg-white">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div>
              <div className="text-sm sm:text-base font-bold text-[#0B132B]">Product mix</div>
              <div className="text-[11px] text-[#6C757D] mt-0.5">Rename each column to match what this dealer actually sells — un-tick "Sold here" to remove a product from the projection.</div>
            </div>
          </div>

          {/* Mobile: stacked, one product per card. Desktop: matrix. */}
          <div className="sm:hidden space-y-3" data-testid="proforma-products-mobile">
            {PRODUCTS.map((p) => {
              const sold = isSold(p);
              return (
                <div key={p.key} className={`rounded-sm px-3 py-3 border ${sold ? "bg-[#F8F9FA] border-[#E9ECEF]" : "bg-[#F1F3F5] border-[#E9ECEF] opacity-70"}`} data-testid={`proforma-product-card-${p.key}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      value={labelFor(p)}
                      onChange={(e) => onLabelChange(p.key, e.target.value)}
                      className="flex-1 h-8 rounded-sm border border-[#E9ECEF] bg-white px-2 text-[11px] uppercase tracking-[0.16em] font-bold text-[#0B132B] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                      data-testid={`proforma-product-label-${p.key}`}
                    />
                    <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] font-bold text-[#0B132B] cursor-pointer whitespace-nowrap">
                      <input type="checkbox" checked={sold} onChange={(e) => onSoldToggle(p.key, e.target.checked)} data-testid={`proforma-product-sold-${p.key}`} />
                      Sold here
                    </label>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#6C757D] mb-1">Premium</div>
                      {editInput(p.premiumRef, "money", !sold)}
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#6C757D] mb-1">Penetration</div>
                      {editInput(p.penetrationRef, "pct", !sold)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="hidden sm:block overflow-x-auto" data-testid="proforma-products-desktop">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr>
                  <th className="text-left text-[10px] uppercase tracking-[0.18em] font-bold text-[#6C757D] py-2 pr-3 w-[190px]">&nbsp;</th>
                  {PRODUCTS.map((p) => (
                    <th key={p.key} className="py-2 px-1" data-testid={`proforma-product-col-${p.key}`}>
                      <input
                        value={labelFor(p)}
                        onChange={(e) => onLabelChange(p.key, e.target.value)}
                        className="w-full h-8 rounded-sm border border-[#E9ECEF] bg-white px-2 text-center text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                        data-testid={`proforma-product-label-${p.key}`}
                      />
                      <label className="flex items-center justify-center gap-1 text-[9px] uppercase tracking-[0.12em] font-bold text-[#0B132B] mt-1 cursor-pointer">
                        <input type="checkbox" checked={isSold(p)} onChange={(e) => onSoldToggle(p.key, e.target.checked)} data-testid={`proforma-product-sold-${p.key}`} />
                        Sold here
                      </label>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="text-sm font-bold text-[#0B132B] py-2 pr-3">Premium Per Product</td>
                  {PRODUCTS.map((p) => (
                    <td key={p.key} className="px-1 py-1">{editInput(p.premiumRef, "money", !isSold(p))}</td>
                  ))}
                </tr>
                <tr>
                  <td className="text-sm font-bold text-[#0B132B] py-2 pr-3">Penetration Per Product</td>
                  {PRODUCTS.map((p) => (
                    <td key={p.key} className="px-1 py-1">{editInput(p.penetrationRef, "pct", !isSold(p))}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Loss ratio */}
        <div className="px-4 sm:px-6 py-4 border-b border-[#E9ECEF] bg-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <div className="text-sm sm:text-base font-bold text-[#0B132B]">Loss Ratio (all products)</div>
              <div className="text-[11px] text-[#6C757D] mt-0.5">Expected claims paid as a share of net written premium</div>
            </div>
            <div className="w-full sm:w-40">{editInput("E17", "pct")}</div>
          </div>
        </div>

        {/* Investment percentage — editable, cascades to Year 1..10 */}
        <div className="px-4 sm:px-6 py-4 bg-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <div className="text-sm sm:text-base font-bold text-[#0B132B]">Investment Percentage</div>
              <div className="text-[11px] text-[#6C757D] mt-0.5">Portfolio yield applied to the reserve — cascades through the full 10-year projection</div>
            </div>
            <div className="w-full sm:w-40">{editInput("E19", "pct")}</div>
          </div>
        </div>
      </Card>

      {/* Calculated outputs */}
      <Card className="p-0 border-[#E9ECEF] rounded-sm overflow-hidden">
        <div className="bg-[#F8F9FA] border-b border-[#E9ECEF] px-4 sm:px-6 py-3">
          <div className="text-xs uppercase tracking-[0.24em] font-bold text-[#0B132B]">10-year projection</div>
        </div>
        {outputRow("E21", "Gross premium")}
        {outputRow("E23", "Cancellations")}
        {outputRow("E25", "Net written premium")}
        {outputRow("E27", "Claims payout")}
        {outputRow("E29", "Net after claims")}
        {outputRow("E31", "Investment income*")}
        {outputRow("E33", "Program expenses")}
        {outputRow("E35", "Total cash generated", { highlight: true })}
        {outputRow("E38", "Total program profit",  { highlight: true })}
      </Card>

      <p className="text-[11px] text-[#6C757D] italic px-2">
        *10-year proforma with a 5-year runoff. Dealer can offset floorplan or facility debt.
      </p>
    </div>
  );
}

// ---------- fallback grid for non-summary sheets (read-only) ----------
function GridSheet({ sheet, computed }) {
  return (
    <Card className="p-0 border-[#E9ECEF] rounded-sm overflow-x-auto" data-testid="proforma-grid">
      <table className="text-xs font-mono border-collapse w-full">
        <tbody>
          {sheet.cells.map((row, ri) => (
            <tr key={`${sheet.name}-row-${ri}`} className={ri === 0 ? "bg-[#0B132B]" : ri % 2 === 0 ? "bg-white" : "bg-[#F8F9FA]"}>
              {row.map((cell) => {
                const isFormula = cell.kind === "formula";
                const isInput = cell.kind === "input";
                const isHeader = ri === 0;
                const cVal = computed?.[sheet.name]?.[cell.ref];
                const displayVal = isInput || isFormula ? (cVal ?? cell.value) : cell.value;
                return (
                  <td key={cell.ref}
                    className={`border border-[#E9ECEF] px-2 py-1.5 whitespace-nowrap ${
                      isHeader ? "text-white font-bold uppercase tracking-[0.08em]" :
                      isFormula ? "text-[#0B132B] font-bold bg-[#F8F9FA]" : "text-[#0B132B]"
                    }`}
                    title={cell.formula ? `Formula: ${cell.formula}` : undefined}>
                    {fmtCell(displayVal, cell.format)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

// ---------- page shell ----------
export default function ProformaEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const [proforma, setProforma] = useState(null);
  const [template, setTemplate] = useState(null);
  const [inputs, setInputs] = useState({});
  const [productLabels, setProductLabels] = useState({});
  const [productSold, setProductSold] = useState({});
  const [dealership, setDealership] = useState("");
  const [prep, setPrep] = useState({ name: "", agency: "", email: "", phone: "" });
  const [hasDealerLogo, setHasDealerLogo] = useState(false);
  const [logoRefresh, setLogoRefresh] = useState(0);
  const logoInputRef = useRef(null);
  const [activeSheet, setActiveSheet] = useState(null);
  const [saving, setSaving] = useState(false);
  const [computed, setComputed] = useState({});
  const [computeStatus, setComputeStatus] = useState("ready");
  const pollTimerRef = React.useRef(null);
  const [emailDialog, setEmailDialog] = useState({ open: false, recipient_email: "", recipient_name: "", message: "", attach_pdf: true, sending: false });

  const load = async () => {
    try {
      const [pRes, tRes] = await Promise.all([
        api.get(`/proformas/${id}`),
        api.get("/proforma/template"),
      ]);
      setProforma(pRes.data);
      setTemplate(tRes.data);
      setInputs(pRes.data.inputs || {});
      setProductLabels(pRes.data.product_labels || {});
      setProductSold(pRes.data.product_sold || {});
      setDealership(pRes.data.dealership || "");
      setPrep({
        name: pRes.data.prep_name || "",
        agency: pRes.data.prep_agency || "",
        email: pRes.data.prep_email || "",
        phone: pRes.data.prep_phone || "",
      });
      setHasDealerLogo(!!pRes.data.has_dealer_logo);
      setComputed(pRes.data.computed || {});
      setComputeStatus(pRes.data.computed_status || "ready");
      const first = tRes.data.sheets?.[0]?.name;
      if (first) setActiveSheet(first);
    } catch (e) {
      console.error("[ProformaEdit] load failed:", e);
      toast.error("Failed to load proforma");
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  // Poll /computed-status until the background xlcalculator run finishes.
  // The recompute takes ~30-40s on 12 sheets, so we back off aggressively.
  useEffect(() => {
    if (computeStatus !== "pending") {
      if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null; }
      return;
    }
    let cancelled = false;
    let delay = 1500;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/proformas/${id}/computed-status`);
        if (cancelled) return;
        if (data.computed_status === "ready") {
          setComputed(data.computed || {});
          setComputeStatus("ready");
          toast.success("Numbers refreshed");
          return;
        }
        if (data.computed_status === "error") {
          setComputeStatus("error");
          toast.error("Recalculation failed — showing last saved values");
          return;
        }
      } catch (err) {
        console.error("[ProformaEdit] status poll failed:", err);
      }
      delay = Math.min(delay + 500, 5000);
      pollTimerRef.current = setTimeout(tick, delay);
    };
    pollTimerRef.current = setTimeout(tick, delay);
    return () => {
      cancelled = true;
      if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null; }
    };
  }, [computeStatus, id]);

  const setInput = (sheet, ref, value) => {
    // Only accept edits for whitelisted cells — belt & suspenders behind the UI gating
    if (!EDITABLE_REFS.has(ref)) return;
    setInputs((prev) => ({ ...prev, [sheet]: { ...(prev[sheet] || {}), [ref]: value } }));
  };

  const setLabel = (key, label) => {
    setProductLabels((prev) => ({ ...prev, [key]: label }));
  };
  const setSold = (key, sold) => {
    setProductSold((prev) => ({ ...prev, [key]: sold }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/proformas/${id}`, {
        inputs,
        dealership: dealership.trim() || null,
        prep_name: prep.name.trim() || null,
        prep_agency: prep.agency.trim() || null,
        prep_email: prep.email.trim() || null,
        prep_phone: prep.phone.trim() || null,
        product_labels: productLabels,
        product_sold: productSold,
      });
      setProforma(data);
      setInputs(data.inputs || {});
      setProductLabels(data.product_labels || {});
      setProductSold(data.product_sold || {});
      setDealership(data.dealership || "");
      setPrep({
        name: data.prep_name || "",
        agency: data.prep_agency || "",
        email: data.prep_email || "",
        phone: data.prep_phone || "",
      });
      setComputed(data.computed || {});
      setComputeStatus(data.computed_status || "ready");
      if (data.computed_status === "pending") {
        toast.success("Saved — recalculating in the background…");
      } else {
        toast.success("Saved");
      }
    } catch (err) {
      console.error("[ProformaEdit] save failed:", err);
      toast.error(err.response?.data?.detail || "Save failed");
    }
    finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/proformas/${id}/dealer-logo`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setHasDealerLogo(true);
      setLogoRefresh((n) => n + 1);
      toast.success("Dealer logo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Logo upload failed");
    }
  };
  const removeLogo = async () => {
    try {
      await api.delete(`/proformas/${id}/dealer-logo`);
      setHasDealerLogo(false);
      setLogoRefresh((n) => n + 1);
      toast.success("Dealer logo removed");
    } catch { toast.error("Failed to remove logo"); }
  };

  const { preview, dialog: pdfDialog } = usePdfPreview();
  const downloadPdf = () => {
    preview({
      fetcher: () => api.get(`/proformas/${id}/pdf`, { responseType: "blob" }),
      filename: `${proforma?.title || "proforma"}.pdf`,
      title: `Proforma · ${proforma?.title || ""}`,
    });
  };

  const openEmailDialog = () => {
    setEmailDialog({ open: true, recipient_email: "", recipient_name: "", message: "", attach_pdf: true, sending: false });
  };
  const sendEmail = async () => {
    if (!emailDialog.recipient_email.trim()) { toast.error("Recipient email is required"); return; }
    setEmailDialog((p) => ({ ...p, sending: true }));
    try {
      await api.post(`/proformas/${id}/email`, {
        recipient_email: emailDialog.recipient_email.trim(),
        recipient_name: emailDialog.recipient_name.trim() || null,
        message: emailDialog.message.trim() || null,
        attach_pdf: !!emailDialog.attach_pdf,
      });
      toast.success(`Proforma emailed to ${emailDialog.recipient_email.trim()}`);
      setEmailDialog({ open: false, recipient_email: "", recipient_name: "", message: "", attach_pdf: true, sending: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Email failed");
      setEmailDialog((p) => ({ ...p, sending: false }));
    }
  };

  const currentSheet = useMemo(
    () => template?.sheets?.find((s) => s.name === activeSheet),
    [template, activeSheet],
  );
  const firstSheetName = template?.sheets?.[0]?.name;
  const isSummary = currentSheet?.name === firstSheetName;

  if (!proforma || !template) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Navbar />
        <main className="max-w-6xl mx-auto px-6 py-10">
          <div className="text-center text-sm text-[#6C757D] py-20">Loading proforma…</div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10 pb-24" data-testid="proforma-edit-page">
        <Link to="/proforma" className="inline-flex items-center gap-2 text-sm font-medium text-[#6C757D] hover:text-[#0B132B] transition-colors mb-4">
          <ArrowLeft size={14} /> Back to proformas
        </Link>

        <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold flex items-center gap-2">
              Proforma
              {computeStatus === "pending" && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#D4AF37]/15 border border-[#D4AF37] rounded-sm text-[10px] font-bold text-[#0B132B] normal-case tracking-normal" data-testid="proforma-recalc-pill">
                  <Loader2 size={10} className="animate-spin"/> Recalculating…
                </span>
              )}
              {computeStatus === "error" && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 border border-red-200 rounded-sm text-[10px] font-bold text-red-700 normal-case tracking-normal" data-testid="proforma-recalc-error">
                  Recalc failed
                </span>
              )}
            </div>
            <h1 className="font-display text-2xl sm:text-4xl font-black text-[#0B132B] mt-1 tracking-tight leading-tight truncate" data-testid="proforma-title">{proforma.title}</h1>
          </div>
          <div className="hidden sm:flex gap-2">
            <Button onClick={save} disabled={saving} className="h-10 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white font-semibold" data-testid="proforma-save-button">
              <Save size={14} className="mr-2" /> {saving ? "Saving…" : "Save & recalc"}
            </Button>
            <Button onClick={openEmailDialog} className="h-10 rounded-sm bg-[#D4AF37] hover:bg-[#B8942F] text-[#0B132B] font-bold" data-testid="proforma-email-button">
              <Mail size={14} className="mr-2" /> Email dealer
            </Button>
            <Button onClick={downloadPdf} variant="outline" className="h-10 rounded-sm border-[#0B132B]/20" data-testid="proforma-pdf-button">
              <Eye size={14} className="mr-2" /> Preview & Download
            </Button>
          </div>
        </div>

        {/* Sheet tabs — horizontally scrollable on mobile */}
        <div className="flex gap-1 overflow-x-auto border-b border-[#E9ECEF] mb-5 -mx-4 sm:mx-0 px-4 sm:px-0" data-testid="proforma-sheet-tabs">
          {template.sheets.map((s) => (
            <button key={s.name} type="button" onClick={() => setActiveSheet(s.name)}
              className={`px-3 sm:px-4 py-2 text-[11px] sm:text-xs uppercase tracking-[0.14em] font-bold whitespace-nowrap border-b-2 transition-colors ${
                activeSheet === s.name ? "border-[#D4AF37] text-[#0B132B]" : "border-transparent text-[#6C757D] hover:text-[#0B132B]"
              }`}
              data-testid={`proforma-sheet-tab-${s.name.replace(/\s+/g, '-')}`}>
              {s.name}
            </button>
          ))}
        </div>

        {isSummary && (
          <Card className="p-0 border-[#E9ECEF] rounded-sm overflow-hidden mb-6" data-testid="proforma-dealer-card">
            <div className="bg-[#F8F9FA] border-b border-[#E9ECEF] px-4 sm:px-6 py-3">
              <div className="text-xs uppercase tracking-[0.24em] font-bold text-[#0B132B]">Prepared for dealer</div>
              <div className="text-[11px] text-[#6C757D] mt-1">Name + logo are printed at the top of the PDF packet.</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-4 px-4 sm:px-6 py-4 bg-white">
              <div>
                <Label className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#0B132B] mb-1 block">Dealer name</Label>
                <Input
                  value={dealership}
                  onChange={(e) => setDealership(e.target.value)}
                  placeholder="e.g. Allstar Ford of Louisville"
                  className="h-11 rounded-sm border-[#E9ECEF]"
                  data-testid="proforma-dealership-input"
                />
                <div className="text-[10px] text-[#6C757D] mt-1">Leave blank to print the proforma title in the header instead.</div>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#0B132B] mb-1 block">Dealer logo</Label>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogo(f); e.target.value = ""; }}
                  className="hidden"
                  data-testid="proforma-dealer-logo-input"
                />
                {hasDealerLogo ? (
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-20 bg-[#F8F9FA] border border-[#E9ECEF] rounded-sm overflow-hidden flex items-center justify-center">
                      <img
                        key={logoRefresh}
                        alt="Dealer logo"
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/proformas/${id}/dealer-logo?t=${logoRefresh}`}
                        className="max-h-10 max-w-full object-contain"
                        data-testid="proforma-dealer-logo-preview"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="h-9 rounded-sm text-xs" onClick={() => logoInputRef.current?.click()} data-testid="proforma-dealer-logo-replace">
                        <Upload size={12} className="mr-1.5" /> Replace
                      </Button>
                      <Button size="sm" variant="outline" className="h-9 rounded-sm text-xs text-red-600 hover:bg-red-50" onClick={removeLogo} data-testid="proforma-dealer-logo-remove">
                        <X size={12} className="mr-1.5" /> Remove
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button variant="outline" onClick={() => logoInputRef.current?.click()} className="h-11 rounded-sm border-dashed border-[#E9ECEF] text-[#6C757D] hover:text-[#0B132B] w-full sm:w-auto" data-testid="proforma-dealer-logo-upload">
                    <ImageIcon size={14} className="mr-2" /> Upload dealer logo
                  </Button>
                )}
                <div className="text-[10px] text-[#6C757D] mt-1">PNG or JPG under 5MB. Optional.</div>
              </div>
            </div>
          </Card>
        )}

        {isSummary && (
          <Card className="p-0 border-[#E9ECEF] rounded-sm overflow-hidden mb-6" data-testid="proforma-prepared-by-card">
            <div className="bg-[#F8F9FA] border-b border-[#E9ECEF] px-4 sm:px-6 py-3">
              <div className="text-xs uppercase tracking-[0.24em] font-bold text-[#0B132B]">Prepared by</div>
              <div className="text-[11px] text-[#6C757D] mt-1">Appears on the cover sheet so the dealer knows who to call. Blank fields default to your account profile.</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-4 sm:px-6 py-4 bg-white">
              <div>
                <Label className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#0B132B] mb-1 block">Name</Label>
                <Input value={prep.name} onChange={(e) => setPrep((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Your name" className="h-11 rounded-sm border-[#E9ECEF]" data-testid="proforma-prep-name" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#0B132B] mb-1 block">Agency</Label>
                <Input value={prep.agency} onChange={(e) => setPrep((p) => ({ ...p, agency: e.target.value }))}
                  placeholder="e.g. Advantage Warranty Group" className="h-11 rounded-sm border-[#E9ECEF]" data-testid="proforma-prep-agency" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#0B132B] mb-1 block">Email</Label>
                <Input type="email" value={prep.email} onChange={(e) => setPrep((p) => ({ ...p, email: e.target.value }))}
                  placeholder="you@example.com" className="h-11 rounded-sm border-[#E9ECEF]" data-testid="proforma-prep-email" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#0B132B] mb-1 block">Phone</Label>
                <Input value={prep.phone} onChange={(e) => setPrep((p) => ({ ...p, phone: e.target.value }))}
                  placeholder="(555) 123-4567" className="h-11 rounded-sm border-[#E9ECEF]" data-testid="proforma-prep-phone" />
              </div>
            </div>
          </Card>
        )}

        {currentSheet && (isSummary
          ? <SummarySheet sheet={currentSheet} inputs={inputs} computed={computed}
              productLabels={productLabels} productSold={productSold}
              onSet={setInput} onLabelChange={setLabel} onSoldToggle={setSold} />
          : (
            <>
              <div className="mb-3 flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#F8F9FA] border border-[#E9ECEF] text-[10px] uppercase tracking-[0.14em] font-bold text-[#6C757D]">Read only</span>
                <span className="text-xs text-[#6C757D]">Calculated presentation view — inputs live on the {firstSheetName} tab · <button type="button" onClick={() => setActiveSheet(firstSheetName)} className="underline text-[#0B132B] hover:text-[#D4AF37]" data-testid="proforma-jump-to-inputs">jump to inputs</button></span>
              </div>
              <GridSheet sheet={currentSheet} computed={computed} />
            </>
          )
        )}
        {pdfDialog}

        {/* Sticky mobile action bar */}
        <div className="sm:hidden fixed bottom-0 left-0 right-0 border-t border-[#E9ECEF] bg-white/95 backdrop-blur px-4 py-3 flex gap-2 z-30" data-testid="proforma-mobile-actions">
          <Button onClick={save} disabled={saving} className="flex-1 h-11 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white font-semibold" data-testid="proforma-save-button-mobile">
            <Save size={14} className="mr-2" /> {saving ? "Saving…" : "Save & recalc"}
          </Button>
          <Button onClick={openEmailDialog} className="h-11 rounded-sm bg-[#D4AF37] hover:bg-[#B8942F] text-[#0B132B] font-bold px-4" data-testid="proforma-email-button-mobile">
            <Mail size={14} />
          </Button>
          <Button onClick={downloadPdf} variant="outline" className="h-11 rounded-sm border-[#0B132B]/20 px-4" data-testid="proforma-pdf-button-mobile">
            <Eye size={14} />
          </Button>
        </div>

        {/* Email-to-dealer dialog */}
        <Dialog open={emailDialog.open} onOpenChange={(o) => !o && setEmailDialog((p) => ({ ...p, open: false }))}>
          <DialogContent className="sm:max-w-lg" data-testid="proforma-email-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl text-[#0B132B]">Email proforma to dealer</DialogTitle>
              <DialogDescription className="text-sm text-[#6C757D]">
                Sends the 10-year projection as a PDF attachment along with a co-branded intro from you.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid gap-2">
                <Label className="text-xs uppercase tracking-[0.16em] font-bold text-[#0B132B]">Recipient email</Label>
                <Input type="email" value={emailDialog.recipient_email}
                  onChange={(e) => setEmailDialog((p) => ({ ...p, recipient_email: e.target.value }))}
                  placeholder="dealer@example.com" required data-testid="proforma-email-recipient"/>
              </div>
              <div className="grid gap-2">
                <Label className="text-xs uppercase tracking-[0.16em] font-bold text-[#0B132B]">Recipient name <span className="text-[#6C757D] font-normal normal-case tracking-normal">(optional)</span></Label>
                <Input value={emailDialog.recipient_name}
                  onChange={(e) => setEmailDialog((p) => ({ ...p, recipient_name: e.target.value }))}
                  placeholder="e.g. John Smith" data-testid="proforma-email-recipient-name"/>
              </div>
              <div className="grid gap-2">
                <Label className="text-xs uppercase tracking-[0.16em] font-bold text-[#0B132B]">Personal note <span className="text-[#6C757D] font-normal normal-case tracking-normal">(optional)</span></Label>
                <textarea value={emailDialog.message}
                  onChange={(e) => setEmailDialog((p) => ({ ...p, message: e.target.value }))}
                  rows={4}
                  className="border border-input rounded-md px-3 py-2 text-sm resize-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  placeholder="Add a personalised message the dealer will see above the profit summary." data-testid="proforma-email-message"/>
              </div>
              <label className="flex items-center gap-2 text-xs text-[#0B132B] cursor-pointer">
                <input type="checkbox" checked={emailDialog.attach_pdf}
                  onChange={(e) => setEmailDialog((p) => ({ ...p, attach_pdf: e.target.checked }))}
                  data-testid="proforma-email-attach"/>
                Attach the proforma PDF (recommended)
              </label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEmailDialog((p) => ({ ...p, open: false }))} disabled={emailDialog.sending}>Cancel</Button>
              <Button type="button" onClick={sendEmail} disabled={emailDialog.sending} className="bg-[#0B132B] hover:bg-[#0B132B]/90 text-white" data-testid="proforma-email-send">
                {emailDialog.sending ? <><Loader2 size={14} className="mr-2 animate-spin"/> Sending…</> : <><Mail size={14} className="mr-2"/> Send email</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
